import { refreshSession } from './sessionRefresh';

/**
 * Reactive 401 → refresh → replay-once safety net for credentialed callers that
 * CANNOT go through the shared axios instance or the Orval `customFetch` mutator.
 *
 * Two legitimate reasons a caller bypasses those clients — and therefore also
 * lost the refresh-and-retry those clients bake in:
 *   1. The notification / cross-tenant domains, which per ADR-020 §6 must NOT
 *      send `X-Resource-Tenant-Id` (both shared clients auto-inject it).
 *   2. Streaming (SSE via `fetch`) and file-upload (`XMLHttpRequest` for
 *      progress events) paths, which axios/Orval don't wire up here.
 *
 * This helper closes that gap without coupling refresh to tenant-header
 * injection. It runs the SAME single-flight `refreshSession()` shared with the
 * axios / orval interceptors and the proactive timer, so a burst of concurrent
 * 401s still triggers exactly one `POST /api/auth/refresh` (avoiding the
 * refresh-token-rotation race — see sessionRefresh.ts).
 *
 * Contract:
 *   - `send` performs the request and resolves with a result carrying an HTTP
 *     status; it is invoked AT MOST TWICE. It MUST rebuild any per-request
 *     headers on each call so the replay picks up the rotated CSRF cookie
 *     (mirrors the axios interceptor, which re-runs on replay).
 *   - On a non-401 first result, that result is returned untouched.
 *   - On a 401, we refresh once. If the refresh fails, the ORIGINAL 401 result
 *     is returned (never a throw) so each caller keeps its own logout/degrade
 *     policy. If the refresh succeeds, `send` is replayed exactly once and its
 *     second result returned — even if that is itself a 401 (a genuinely dead
 *     session cannot loop).
 *   - Rejections from `send` (network error, aborted request) propagate as-is.
 *
 * @param send      Thunk that issues the request and resolves with a result.
 * @param getStatus Reads the HTTP status from a result (e.g. `r => r.status`).
 * @param opts.skip Skip the refresh path entirely (e.g. auth-lifecycle URLs
 *                  such as /refresh or /logout, where retrying would recurse).
 */
export async function retryOn401<T>(
  send: () => Promise<T>,
  getStatus: (result: T) => number,
  opts: { skip?: boolean } = {}
): Promise<T> {
  const first = await send();
  if (opts.skip || getStatus(first) !== 401) {
    return first;
  }

  const refresh = await refreshSession();
  if (!refresh.ok) {
    // Refresh failed — the session is genuinely gone. Hand the original 401
    // back so the caller applies its own policy (logout, degrade, surface).
    return first;
  }

  // Fresh access cookie in place — replay exactly once.
  return send();
}
